/**
 * Tyaus - Main Application Component
 */

import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './stores/auth';
import { usePreferencesStore } from './stores/preferences';
import { startSsoLogin } from './utils/ssoAuth';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import LoggedOutPage from './pages/LoggedOutPage';
import DashboardPage from './pages/DashboardPage';
import JobsPage from './pages/JobsPage';
import JobDetailPage from './pages/JobDetailPage';
import ApiKeysPage from './pages/ApiKeysPage';
import ServerlessPage from './pages/ServerlessPage';
import InfrastructurePage from './pages/InfrastructurePage';
import NetworkPage from './pages/NetworkPage';
import OperationsPage from './pages/OperationsHubPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import HelpPage from './pages/HelpPage';
import AdminUsersPage from './pages/AdminUsersPage';
import EndpointsPage from './pages/EndpointsPage';
import FinancialDashboardPage from './pages/FinancialDashboardPage';
import PayoutsAdminPage from './pages/PayoutsAdminPage';

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-surface-950 flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-surface-400 text-sm">Redirecting...</p>
      </div>
    </div>
  );
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated) {
      const returnTo = location.pathname + location.search;
      const dest = returnTo === '/auth/callback' ? '/dashboard' : returnTo;
      startSsoLogin(dest);
    }
  }, [isAuthenticated, location]);

  if (!isAuthenticated) {
    return <LoadingScreen />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated) {
      const returnTo = location.pathname + location.search;
      startSsoLogin(returnTo);
    }
  }, [isAuthenticated, location]);

  if (!isAuthenticated) {
    return <LoadingScreen />;
  }

  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function App() {
  const initializePreferences = usePreferencesStore((state) => state.initializePreferences);

  useEffect(() => {
    initializePreferences();
  }, [initializePreferences]);

  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/logged-out" element={<LoggedOutPage />} />
      {/* /login triggers SSO redirect — kept so deep-link ?returnTo= params are preserved */}
      <Route path="/login" element={<LoginPage />} />
      {/* Signup, activation, password reset are handled by auth.tyaus.com */}
      <Route path="/signup" element={<Navigate to="/" replace />} />
      <Route path="/activate" element={<Navigate to="/" replace />} />
      <Route path="/forgot-password" element={<Navigate to="/" replace />} />
      <Route path="/reset-password" element={<Navigate to="/" replace />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="jobs" element={<JobsPage />} />
        <Route path="jobs/:jobId" element={<JobDetailPage />} />
        <Route path="serverless" element={<ServerlessPage />} />
        <Route path="infrastructure" element={<InfrastructurePage />} />
        <Route path="network" element={<NetworkPage />} />
        <Route path="operations" element={<OperationsPage />} />
        <Route path="api-keys" element={<ApiKeysPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="finance" element={<FinancialDashboardPage />} />
        <Route path="payouts" element={<PayoutsAdminPage />} />
        <Route
          path="admin/users"
          element={
            <AdminRoute>
              <AdminUsersPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/endpoints"
          element={
            <AdminRoute>
              <EndpointsPage />
            </AdminRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
